import { DownOutlined, GlobalOutlined, SearchOutlined, ShoppingCartOutlined, UserOutlined } from '@ant-design/icons';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Avatar } from 'antd';
import { SvgIcon } from 'src/components/atoms/SvgIcon';
import HeaderModal from 'src/components/popups/HeaderModal';
import LoginModal from 'src/components/popups/LoginModal';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { resetImagesArr, setLng } from 'src/store/app/actions';
import { getUser, logout } from 'src/store/user/actions';
import { IRootState } from 'src/types/store-types';
import { scrollToTop } from 'src/utils/helpers';
import './styles.scss';

const LANGUAGE_OPTIONS = [
  { key: 'en', label: 'Eng', flag: '🇬🇧' },
  { key: 'ru', label: 'Рус', flag: '🇷🇺' },
  { key: 'am', label: 'Հայ', flag: '🇦🇲' },
];
const SUPPORTED_LANGUAGES = LANGUAGE_OPTIONS.map((language) => language.key);

const getLanguageOption = (lng: string): (typeof LANGUAGE_OPTIONS)[number] =>
  LANGUAGE_OPTIONS.find((language) => language.key.toLowerCase() === lng.toLowerCase()) || LANGUAGE_OPTIONS[0];

const getPathWithLanguage = (pathname: string, lng: string): string => {
  const pathParts = pathname.split('/').filter(Boolean);
  const hasLanguagePrefix = SUPPORTED_LANGUAGES.includes(pathParts[0]);

  if (hasLanguagePrefix) {
    pathParts[0] = lng;
  } else {
    pathParts.unshift(lng);
  }

  return `/${pathParts.join('/')}`;
};

const Header: FC = () => {
  const { t, i18n } = useTranslation();
  const resetImagesArrAction = useConnectedAction(resetImagesArr);
  const setLngAction = useConnectedAction(setLng);
  const location = useLocation();
  const navigate = useNavigate();

  let { lng } = useSelector((state: IRootState) => state.app);
  const { cartCount } = useSelector((state: IRootState) => state.tours);

  lng = lng.toLowerCase();
  const [activetab, setActive] = useState(lng);
  const [visibleModal, setVisibleModal] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [isLoginModalVisible, setLoginModalVisible] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const { token, user } = useSelector((state: IRootState) => state.users);
  const _getUser = useConnectedAction(getUser);
  const _logout = useConnectedAction(logout);

  // Fetch user data when token exists but user data is missing
  useEffect(() => {
    if (token && !user) {
      _getUser();
    }
  }, [token, user, _getUser]);

  const handleUserClick = (): void => {
    if (!user) {
      setLoginModalVisible(true);
      return;
    }
    setShowDropdown((prev) => !prev);
  };

  const closeDropdown = (): void => {
    setShowDropdown(false);
  };

  const handleLogout = (): void => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = (): void => {
    _logout();
    setShowDropdown(false);
    setShowLogoutConfirm(false);
  };

  const cancelLogout = (): void => {
    setShowLogoutConfirm(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const dropdown = document.getElementById('user-dropdown');
      const userIconContainer = document.querySelector('.user-icon-container');

      if (dropdown && !dropdown.contains(e.target as Node) && !userIconContainer?.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      const target = e.target as Node;
      const dropdowns = Array.from(document.querySelectorAll('.language-dropdown'));
      const languageContainers = Array.from(document.querySelectorAll('.language-dropdown-container'));
      const isInsideDropdown = dropdowns.some((dropdown) => dropdown.contains(target));
      const isInsideLanguageContainer = languageContainers.some((container) => container.contains(target));

      if (!isInsideDropdown && !isInsideLanguageContainer) {
        setShowLanguageDropdown(false);
      }
    };

    if (showLanguageDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [showLanguageDropdown]);

  useEffect(() => {
    const handleResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    setActive(lng);
  }, [lng]);

  const handleSelectLng = (lng: string): void => {
    setLngAction(lng);
    i18n.changeLanguage(lng);
    setActive(lng);
    setShowLanguageDropdown(false);
    navigate({ pathname: getPathWithLanguage(location.pathname, lng), search: location.search, hash: location.hash });
  };
  const toogleList = (): void => {
    setVisibleModal((state: boolean) => !state);
  };

  const closeHeaderModal = (): void => {
    if (visibleModal) {
      setVisibleModal(false);
    }
  };

  const onLogoClick = (): void => {
    resetImagesArrAction();
    closeHeaderModal();
    scrollToTop();
  };

  const activeLanguage = getLanguageOption(activetab);
  const renderLanguageDropdown = (className = ''): JSX.Element => {
    const containerClassName = ['language-dropdown-container', className].filter(Boolean).join(' ');
    const languageArrowClassName = `language-arrow${showLanguageDropdown ? ' open' : ''}`;

    return (
      <div className={containerClassName}>
        <button
          className="language-dropdown-trigger"
          type="button"
          onClick={() => setShowLanguageDropdown((state) => !state)}
        >
          <GlobalOutlined className="language-icon" />
          <span className="language-flag">{activeLanguage.flag}</span>
          <span className="language-label">{activeLanguage.label}</span>
          <DownOutlined className={languageArrowClassName} />
        </button>
        {showLanguageDropdown && (
          <div className="language-dropdown">
            {LANGUAGE_OPTIONS.map((language) => (
              <button
                key={language.key}
                className={`language-dropdown-item${
                  activetab.toLowerCase() === language.key.toLowerCase() ? ' active' : ''
                }`}
                type="button"
                onClick={() => handleSelectLng(language.key)}
              >
                <span className="language-flag">{language.flag}</span>
                <span>{language.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <header className="header">
      <div className="header-logo-container">
        <Link to={`/${lng}`} onClick={onLogoClick} aria-label="tourest.online">
          <SvgIcon className="logo" src={'/images/logo.svg'} />
        </Link>
      </div>
      <div className="header-rigth-container">
        <div className="header-content left link" style={{ display: width > 800 ? 'flex' : 'none' }}>
          <div className="header-content-tab" style={{ display: width > 800 ? 'flex' : 'none' }}>
            <Link className="header-icon-link link" to={`/${lng}/search`} aria-label={String(t('search'))}>
              <SearchOutlined />
            </Link>
            <div className="header-content-border-bottom"></div>
          </div>
          <div className="header-content-tab" style={{ display: width > 800 ? 'flex' : 'none' }}>
            {cartCount > 0 && <div className="badge">{cartCount}</div>}
            <Link className="header-icon-link link" to={`/${lng}/cart`} aria-label={String(t('cart'))}>
              <ShoppingCartOutlined />
            </Link>
            <div className="header-content-border-bottom"></div>
          </div>
          <div className="header-content language-container link" style={{ display: width > 800 ? 'flex' : 'none' }}>
            {width > 800 && renderLanguageDropdown()}
          </div>
        </div>
      </div>

      {/* for mobile */}
      <div className="header-menu-container link">
        <div className="header-menu-container_content">
          <div className="header-content-tab">
            {cartCount > 0 && <div className="badge">{cartCount}</div>}
            <Link
              className="header-icon-link link"
              to={`/${lng}/cart`}
              onClick={closeHeaderModal}
              aria-label={String(t('cart'))}
            >
              <ShoppingCartOutlined />
            </Link>
            <div className="header-content-border-bottom"></div>
          </div>
          {width <= 800 && renderLanguageDropdown('mobile-language-dropdown')}
          <div className="header-menu-icon" onClick={() => toogleList()}>
            <div className="header-icon" style={{ width: 22, height: 30 }}>
              <img src="/images/icons/menu.png" alt="menu" />
            </div>
          </div>
        </div>
      </div>
      {width <= 800 && <HeaderModal visibleModal={visibleModal} setVisibleModal={setVisibleModal} lng={lng} />}

      <div className="user-icon-container" onClick={handleUserClick}>
        {user?.profilePictureUrl ? (
          <Avatar size={32} src={user?.profilePictureUrl} />
        ) : (
          <Avatar size={32}>
            <UserOutlined size={16} />
          </Avatar>
        )}
        {showDropdown && user && (
          <div id="user-dropdown" className="user-dropdown">
            <div className="dropdown-section">
              <span onClick={closeDropdown} className="dropdown-item span">
                {user.profilePictureUrl ? (
                  <img src={user.profilePictureUrl} className="user-avatar" />
                ) : (
                  <SvgIcon className="dropdown-icon" src={'/images/icons/tourIcon.svg'} />
                )}
                {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : t('profile')}
              </span>
              <Link to={`/${lng}/profile/bonuses`} onClick={closeDropdown} className="dropdown-item">
                <SvgIcon className="dropdown-icon" src={'/images/icons/crown.svg'} />
                {t('bonuses')}
              </Link>
            </div>
            <div className="dropdown-divider"></div>
            <div className="dropdown-section">
              <button onClick={handleLogout} className="logout-btn">
                {t('logout')}
              </button>
            </div>
          </div>
        )}
      </div>
      <LoginModal visible={isLoginModalVisible} onClose={() => setLoginModalVisible(false)} />

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="logout-modal-overlay">
          <div className="logout-modal">
            <h3>{t('confirmLogout') || 'Are you sure you want to logout?'}</h3>
            <div className="logout-modal-actions">
              <button onClick={cancelLogout} className="cancel-btn">
                {t('cancel') || 'Cancel'}
              </button>
              <button onClick={confirmLogout} className="confirm-btn">
                {t('confirm') || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
